import { useSelector } from 'react-redux';
import type { CustomTheme } from '../MaperTheme';
import { darkTheme } from '../themes/darkTheme';
import { lightTheme } from '../themes/lightTheme';

/**
 * Custom hook to access Maper theme with custom tokens
 *
 * Provides typed access to all custom health status tokens
 * defined in CustomTokens, including:
 * - colorHealthOptimal1Bg, colorHealthOptimal1Fg, colorHealthOptimal1Stroke
 * - colorHealthAcceptable1Bg, colorHealthAcceptable1Fg, colorHealthAcceptable1Stroke
 * - colorHealthAttention1Bg, colorHealthAttention1Fg, colorHealthAttention1Stroke
 * - colorHealthDanger1Bg, colorHealthDanger1Fg, colorHealthDanger1Stroke
 * - colorHealthUnknown1Bg, colorHealthUnknown1Fg, colorHealthUnknown1Stroke
 * - And their secondary variants (colorHealth*2Bg, colorHealth*2Fg, colorHealth*2Stroke)
 *
 * The hook retrieves the theme from Redux store and returns the corresponding theme
 * (dark or light) with all custom tokens. The theme is properly typed as CustomTheme
 * to provide TypeScript autocomplete and type checking for all custom tokens.
 *
 * @returns CustomTheme with all custom health status tokens
 *
 * @example
 * ```tsx
 * import { useMaperTheme } from '../../../theme/hooks/useMaperTheme';
 *
 * const MyComponent = () => {
 *     const theme = useMaperTheme();
 *     const optimalColor = theme.colorHealthOptimal1Bg;
 *     return <div style={{ backgroundColor: optimalColor }}>Health Status</div>;
 * };
 * ```
 */
export const useMaperTheme = (): CustomTheme => {
    const themeMode = useSelector((state: any) => state.ThemeOptions.theme);
    const theme = themeMode === 'dark' ? darkTheme : lightTheme;
    return theme;
};
